/**
 * Forgot Password Page
 * Handles password reset with security phrase validation
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getFailoverAwareAuth } from '../../config/firebaseWithFailover';
import Logger from '../../services/logger';
import { getSupportEmail } from '../../utils/supportEmail';
import logoImg from '../../assets/logo.png';
import styles from './Login.module.css';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'securityPhrase' | 'noPhrase'>('email');
  const [email, setEmail] = useState('');
  const [securityPhrase, setSecurityPhrase] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const normalizedEmail = email.toLowerCase().trim();
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../config/firebase');
      const lookupPasswordResetUserCallable = httpsCallable(functions, 'generateSecurityPhraseChangeToken');

      const lookupResult = await lookupPasswordResetUserCallable({
        lookupOnly: true,
        email: normalizedEmail
      }) as {
        data: {
          success: boolean;
          found?: boolean;
          userId?: string;
          hasSecurityPhrase?: boolean;
          email?: string;
        }
      };

      if (!lookupResult.data.found || !lookupResult.data.userId) {
        // Don't reveal if user exists - security best practice
        setSuccess('If an account with that email exists, you will receive password reset instructions.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      const foundUserId = lookupResult.data.userId;
      const resolvedEmail = lookupResult.data.email || normalizedEmail;
      setUserId(foundUserId);

      // Keep using normalized email value for downstream email operations.
      setEmail(resolvedEmail);

      if (lookupResult.data.hasSecurityPhrase) {
        // User has security phrase - ask for it
        setStep('securityPhrase');
      } else {
        // User doesn't have security phrase - send email link to set it and reset password
        setStep('noPhrase');
        await sendNoPhraseEmail(foundUserId, resolvedEmail);
      }
    } catch (error) {
      Logger.error('[ForgotPassword] Error finding user:', error as Error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSecurityPhraseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!userId) {
      setError('Session expired. Please start over.');
      setStep('email');
      setLoading(false);
      return;
    }

    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../config/firebase');
      const validateSecurityPhraseCallable = httpsCallable(functions, 'verifySecurityPhraseChangeToken');
      const validationResult = await validateSecurityPhraseCallable({
        validateOnly: true,
        userId,
        securityPhrase
      }) as {
        data: {
          success: boolean;
          locked?: boolean;
          error?: string;
          attemptsRemaining?: number;
        }
      };
      const validation = validationResult.data;

      if (validation.success) {
        // Security phrase is correct - send password reset email
        const auth = getFailoverAwareAuth();
        await sendPasswordResetEmail(auth, email);
        
        setSuccess('Password reset email sent! Check your inbox for instructions.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        if (validation.locked) {
          setError(validation.error || 'Account locked due to too many failed attempts.');
        } else {
          setError(validation.error || 'Invalid security phrase');
          if (validation.attemptsRemaining !== undefined) {
            setError(`${validation.error} (${validation.attemptsRemaining} attempts remaining)`);
          }
        }
      }
    } catch (error: any) {
      Logger.error('[ForgotPassword] Error validating security phrase:', error as Error);
      
      if (error.code === 'auth/user-not-found') {
        setError('User not found. Please check your email address.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const sendNoPhraseEmail = async (userId: string, userEmail: string) => {
    try {
      // Generate token using Cloud Function (bypasses Firestore rules for unauthenticated users)
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../config/firebase');
      const generateTokenCallable = httpsCallable(functions, 'generateSecurityPhraseChangeToken');
      
      const tokenResult = await generateTokenCallable({
        userId,
        email: userEmail
      }) as { data: { success: boolean; token?: string; error?: string } };
      
      if (!tokenResult.data.success || !tokenResult.data.token) {
        throw new Error(tokenResult.data.error || 'Failed to generate token');
      }
      
      const token = tokenResult.data.token;

      // Call Firebase Function to send email (reuse already imported httpsCallable and functions)
      const sendNoPhrasePasswordResetEmailCallable = httpsCallable(functions, 'sendNoPhrasePasswordResetEmail');
      
      const resetUrl = `${window.location.origin}/change-security-phrase?token=${token}&resetPassword=true`;
      
      await sendNoPhrasePasswordResetEmailCallable({
        email: userEmail,
        resetUrl,
        userId
      });
      
      setSuccess('Email sent! Click the link in your email to set your security phrase and reset your password.');
      
      Logger.info('[ForgotPassword] No phrase password reset email sent', { userId, email: userEmail });
    } catch (error) {
      Logger.error('[ForgotPassword] Error sending no phrase email:', error as Error);
      setError('Failed to send email. Please try again or contact support.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password - Tranzit.io</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className={styles.loginContainer}>
        <header className={styles.header}>
          <Link to="/" className={styles.logo} style={{ textDecoration: 'none' }}>
            <img src={logoImg} alt="Tranzit.io Logo" className={styles.logoImg} loading="eager" width="200" height="50" />
            <div className={styles.textLogo}>
              <span>T</span>
              <span>R</span>
              <span>A</span>
              <span>N</span>
              <span>Z</span>
              <span>I</span>
              <span>T</span>
            </div>
          </Link>
          <nav className={styles.nav}>
            <Link className={styles.menuLink} to="/">Home</Link>
            <Link className={styles.menuLink} to="/login">Login</Link>
            <Link className={styles.menuLink} to="/register">Register</Link>
          </nav>
        </header>
        
        <div className={styles.loginContent}>
          <div className={styles.loginBox}>
            <h1>Reset Password</h1>
            
            {error && <div className={styles.error}>{error}</div>}
            {success && <div style={{ color: '#28a745', padding: '12px', marginBottom: '16px', background: '#d4edda', borderRadius: '4px' }}>{success}</div>}
            
            {step === 'email' && (
              <form onSubmit={handleEmailSubmit} className={styles.form}>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  Enter your email address. If you have a security phrase set, you'll be asked to enter it.
                </p>
                
                <div className={styles.inputGroup}>
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={styles.input}
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                  />
                </div>
                
                <button type="submit" className={styles.loginButton} disabled={loading}>
                  {loading ? 'Processing...' : 'Continue'}
                </button>
                
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <Link to="/login" style={{ color: '#007bff', textDecoration: 'none' }}>
                    Back to Login
                  </Link>
                </div>
                
                <div style={{ marginTop: '16px', padding: '12px', background: '#fff3cd', borderRadius: '4px', fontSize: '14px' }}>
                  <p style={{ margin: 0, color: '#856404' }}>
                    <strong>Need help?</strong> If you can't remember your security phrase, contact customer care at{' '}
                    <a href={`mailto:${getSupportEmail()}`} style={{ color: '#856404' }}>{getSupportEmail()}</a>
                  </p>
                </div>
              </form>
            )}
            
            {step === 'securityPhrase' && (
              <form onSubmit={handleSecurityPhraseSubmit} className={styles.form}>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  Enter your 3-word security phrase to continue with password reset.
                </p>
                
                <div className={styles.inputGroup}>
                  <label htmlFor="securityPhrase">Security Phrase</label>
                  <input
                    type="text"
                    id="securityPhrase"
                    value={securityPhrase}
                    onChange={(e) => setSecurityPhrase(e.target.value)}
                    className={styles.input}
                    placeholder="Enter your 3-word security phrase"
                    required
                    disabled={loading}
                  />
                </div>
                
                <button type="submit" className={styles.loginButton} disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify & Reset Password'}
                </button>
                
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setSecurityPhrase('');
                      setError('');
                    }}
                    style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Back
                  </button>
                </div>
                
                <div style={{ marginTop: '16px', padding: '12px', background: '#fff3cd', borderRadius: '4px', fontSize: '14px' }}>
                  <p style={{ margin: 0, color: '#856404' }}>
                    <strong>Can't remember your security phrase?</strong> Contact customer care at{' '}
                    <a href={`mailto:${getSupportEmail()}`} style={{ color: '#856404' }}>{getSupportEmail()}</a>
                  </p>
                </div>
              </form>
            )}
            
            {step === 'noPhrase' && (
              <div className={styles.form}>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  You don't have a security phrase set. We've sent you an email with a link to set your security phrase and reset your password.
                </p>
                
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <Link to="/login" className={styles.loginButton} style={{ display: 'inline-block', textDecoration: 'none' }}>
                    Back to Login
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
